import React from 'react';
import { Link } from 'react-router-dom';
import { FadeIn, SectionTitle, Btn, Card } from '../components/UI';
import SERVICES_DATA from '../data/services';
import logo from '../assets/logo.svg';

export default function HomePage({ content = {} }) {
  return (
    <div>
      {/* ── Hero ───────────────────────────────────────────────────────── */}
      <section style={{
        minHeight: '100vh', display: 'flex', flexDirection: 'column',
        justifyContent: 'center', alignItems: 'center', textAlign: 'center',
        padding: '120px 24px 80px',
        background: 'radial-gradient(ellipse at 25% 15%, rgba(33,150,243,0.07) 0%, transparent 55%), radial-gradient(ellipse at 75% 85%, rgba(41,182,246,0.05) 0%, transparent 50%), var(--dark)',
        position: 'relative', overflow: 'hidden',
      }}>
        <div style={{
          position: 'absolute', inset: 0, opacity: 0.025,
          backgroundImage: 'linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)',
          backgroundSize: '64px 64px',
        }} />

        <FadeIn>
          <img src={logo} alt="KnowIT Solutions" style={{
            height: 56, width: 'auto',
          }} />
        </FadeIn>

        <FadeIn delay={0.12}>
          <p style={{
            fontFamily: 'var(--font-mono)', fontSize: '0.78rem',
            color: 'var(--cyan)', textTransform: 'uppercase',
            letterSpacing: '0.2em', margin: '28px 0 20px',
          }}>
            {content.subtitle || 'IT Consultancy • Solutions • Services'}
          </p>
        </FadeIn>

        <FadeIn delay={0.24}>
          <h1 style={{
            fontFamily: 'var(--font-body)',
            fontSize: 'clamp(2.2rem, 5.5vw, 4.5rem)',
            fontWeight: 700, color: '#fff', lineHeight: 1.08,
            letterSpacing: '-0.025em', maxWidth: 820, margin: '0 auto 22px',
          }}>
            {content.heroTitle || 'Smart technology solutions for modern business'}
          </h1>
        </FadeIn>

        <FadeIn delay={0.36}>
          <p style={{
            fontFamily: 'var(--font-body)', fontSize: '1.1rem', fontWeight: 300,
            color: 'var(--text-secondary)', maxWidth: 540,
            lineHeight: 1.75, marginBottom: 36,
          }}>
            {content.heroLead || 'From bespoke web design and internal systems to Azure cloud services and disaster recovery — we deliver IT that works for you.'}
          </p>
        </FadeIn>

        <FadeIn delay={0.48}>
          <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', justifyContent: 'center' }}>
            <Link to="/services"><Btn large>Explore Our Services</Btn></Link>
            <Link to="/contact"><Btn outline large>Get In Touch</Btn></Link>
          </div>
        </FadeIn>

        <FadeIn delay={0.6} style={{ marginTop: 80, width: '100%', maxWidth: 780 }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
            gap: 24,
          }}>
            {[
              { num: '15+', label: 'Years Experience' },
              { num: '200+', label: 'Projects Delivered' },
              { num: '99.9%', label: 'Uptime Guaranteed' },
              { num: '50+', label: 'Happy Clients' },
            ].map((s, i) => (
              <div key={i} style={{ textAlign: 'center' }}>
                <div style={{
                  fontFamily: 'var(--font-body)', fontSize: '2rem',
                  fontWeight: 700, color: 'var(--cyan)',
                }}>{s.num}</div>
                <div style={{
                  fontFamily: 'var(--font-body)', fontSize: '0.75rem',
                  fontWeight: 400, color: 'var(--text-muted)',
                  marginTop: 4, textTransform: 'uppercase', letterSpacing: '0.1em',
                }}>{s.label}</div>
              </div>
            ))}
          </div>
        </FadeIn>
      </section>

      {/* ── Services Preview ───────────────────────────────────────────── */}
      <section style={{ padding: '100px 40px', background: 'var(--dark)' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          <FadeIn>
            <SectionTitle subtitle="What We Do" title="End-to-end IT services" />
          </FadeIn>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: 20,
          }}>
            {SERVICES_DATA.map((s, i) => (
              <FadeIn key={i} delay={i * 0.07}>
                <Card>
                  <div style={{ fontSize: '1.8rem', marginBottom: 14 }}>{s.icon}</div>
                  <h3 style={{
                    fontFamily: 'var(--font-body)', fontSize: '1.1rem',
                    fontWeight: 600, color: 'var(--text-primary)', marginBottom: 8,
                  }}>{s.title}</h3>
                  <p style={{
                    fontFamily: 'var(--font-body)', fontSize: '0.88rem',
                    fontWeight: 300, color: 'var(--text-secondary)', lineHeight: 1.65,
                  }}>{s.short}</p>
                </Card>
              </FadeIn>
            ))}
          </div>
          <FadeIn delay={0.3} style={{ textAlign: 'center', marginTop: 44 }}>
            <Link to="/services"><Btn>View All Services</Btn></Link>
          </FadeIn>
        </div>
      </section>

      {/* ── CTA ────────────────────────────────────────────────────────── */}
      <section style={{
        padding: '100px 40px', textAlign: 'center',
        background: 'linear-gradient(180deg, var(--dark) 0%, #0a1428 100%)',
      }}>
        <FadeIn>
          <h2 style={{
            fontFamily: 'var(--font-body)',
            fontSize: 'clamp(1.7rem, 3.5vw, 2.8rem)',
            fontWeight: 700, color: '#fff', marginBottom: 18,
          }}>
            {content.ctaTitle || 'Ready to transform your IT?'}
          </h2>
          <p style={{
            fontFamily: 'var(--font-body)', fontWeight: 300,
            color: 'var(--text-secondary)', fontSize: '1.05rem',
            maxWidth: 480, margin: '0 auto 30px', lineHeight: 1.7,
          }}>
            {content.ctaText || "Let's discuss how we can help your business leverage technology for real results."}
          </p>
          <Link to="/contact"><Btn large>Start a Conversation</Btn></Link>
        </FadeIn>
      </section>
    </div>
  );
}
